import { ReactNode, useState } from 'react';

import { Collapse, FormControlLabel } from '@mui/material';
import { useField } from 'react-final-form';

import {
  Button,
  Icons,
  Typography,
  Stack,
  InfoTooltip,
  Box,
  tokens,
} from '@cast/design-system';

import {
  AGGRESSIVE_MODE_OPTION_LABELS,
  KENT_AGGRESSIVE_MODE_OPTION_TOOLTIP_TEXTS,
  MAX_DRAIN_TIMEOUT_MINUTES,
  MIN_DRAIN_TIMEOUT_MINUTES,
} from 'features/cluster/rebalance/constants';
import { AggressiveModeIgnorePvPodsAlert } from 'features/cluster/rebalance/components/AggressiveModeIgnorePvPodsAlert';

import { FIELD_NAMES } from '../../_constants';
import { FormValues } from '../../_types';

import { RffCheckbox, RffInput, RffSwitch } from '@cast/shared-rff-ui';

const ConfigItem = ({
  title,
  name,
  testId,
  warning,
  tooltipText,
}: {
  title: string;
  name: string;
  testId: string;
  warning?: boolean;
  tooltipText?: ReactNode;
}) => {
  return (
    <FormControlLabel
      sx={{
        ml: 0,
        gap: '8px',
      }}
      control={<RffCheckbox name={name} size="small" testId={testId} />}
      label={
        <Stack direction="row" alignItems="center" gap={4}>
          <Typography variant="P12M">{title}</Typography>
          {tooltipText && <InfoTooltip title={tooltipText} />}
          {warning && (
            <Icons.Warning
              size={16}
              weight="fill"
              color={tokens.theme.bg.status.warningStrong}
            />
          )}
        </Stack>
      }
    />
  );
};

const SwitchItem = ({
  title,
  name,
  testId,
  tooltipText,
}: {
  title: string;
  name: string;
  tooltipText?: ReactNode;
  testId: string;
}) => {
  return (
    <FormControlLabel
      sx={{
        ml: 0,
        gap: 8,
      }}
      control={<RffSwitch name={name} size="sm" testId={testId} />}
      label={
        <Stack direction="row" alignItems="center" gap={8}>
          <Typography variant="P12M">{title}</Typography>
          {tooltipText && <InfoTooltip title={tooltipText} />}
        </Stack>
      }
    />
  );
};

const getPrefixedFieldName = (fieldName: string) => {
  return `${FIELD_NAMES.aggressiveModeConfig}.${fieldName}`;
};

const AggressiveModeConfig = () => {
  const [isExpanded, setIsExpanded] = useState(false);

  const { meta: drainTimeoutMeta } = useField(
    getPrefixedFieldName(FIELD_NAMES.aggressiveModeConfigDrainTimeoutMinutes)
  );

  const hasVisibleConfigError =
    !!drainTimeoutMeta.error && drainTimeoutMeta.touched;
  const isConfigExpanded = isExpanded || hasVisibleConfigError;

  const { input: aggressiveEvictionInput } = useField<boolean>(
    getPrefixedFieldName(FIELD_NAMES.aggressiveModeConfigAggressiveEviction)
  );
  const { input: ignoreLocalPVInput } = useField<boolean>(
    getPrefixedFieldName(
      FIELD_NAMES.aggressiveModeConfigIgnoreLocalPersistentVolumes
    )
  );

  return (
    <Stack ml={24}>
      <Button
        variant="text"
        size="md"
        endIcon={isConfigExpanded ? <Icons.CaretUp /> : <Icons.CaretDown />}
        onClick={() => setIsExpanded(!isExpanded)}
        sx={{ mb: 8 }}
        data-testid="aggressive-mode-expand-button"
      >
        Advanced configuration
      </Button>

      <Collapse in={isConfigExpanded}>
        <Stack
          bgcolor="grey.100"
          p="12px 16px 12px 16px"
          borderRadius="4px"
          gap={16}
          data-testid="aggressive-mode-config"
        >
          <SwitchItem
            title={AGGRESSIVE_MODE_OPTION_LABELS.aggressiveEviction}
            name={getPrefixedFieldName(
              FIELD_NAMES.aggressiveModeConfigAggressiveEviction
            )}
            testId="aggressive-eviction"
            tooltipText={
              KENT_AGGRESSIVE_MODE_OPTION_TOOLTIP_TEXTS.aggressiveEviction
            }
          />
          {aggressiveEvictionInput.value && (
            <Box ml={34}>
              <RffInput
                type="number"
                label={
                  <Stack direction="row" alignItems="center" gap={4}>
                    <Typography variant="P12M">Drain timeout</Typography>
                    <InfoTooltip
                      title={
                        KENT_AGGRESSIVE_MODE_OPTION_TOOLTIP_TEXTS.drainTimeoutSeconds
                      }
                    />
                  </Stack>
                }
                name={getPrefixedFieldName(
                  FIELD_NAMES.aggressiveModeConfigDrainTimeoutMinutes
                )}
                size="small"
                testId="drain-timeout-minutes"
                sx={{ width: 100 }}
                min={MIN_DRAIN_TIMEOUT_MINUTES}
                max={MAX_DRAIN_TIMEOUT_MINUTES}
                hint={`minutes (${MIN_DRAIN_TIMEOUT_MINUTES}–${MAX_DRAIN_TIMEOUT_MINUTES})`}
              />
            </Box>
          )}
          <SwitchItem
            title={
              AGGRESSIVE_MODE_OPTION_LABELS.ignoreZeroNodePoolDisruptionBudgets
            }
            name={getPrefixedFieldName(
              FIELD_NAMES.aggressiveModeConfigIgnoreZeroNodePoolDisruptionBudgets
            )}
            testId="ignore-zero-node-pool-disruption-budgets"
            tooltipText={
              KENT_AGGRESSIVE_MODE_OPTION_TOOLTIP_TEXTS.ignoreZeroNodePoolDisruptionBudgets
            }
          />
          <Stack>
            <Typography variant="P12R" mb={12}>
              Rebalance a node even if it is marked as:
            </Typography>
            <Stack gap={12}>
              <ConfigItem
                title={
                  AGGRESSIVE_MODE_OPTION_LABELS.ignoreProblemRemovalDisabledNodes
                }
                name={getPrefixedFieldName(
                  FIELD_NAMES.aggressiveModeConfigIgnoreProblemRemovalDisabledNodes
                )}
                testId="ignore-problem-removal-disabled-nodes"
                tooltipText={
                  KENT_AGGRESSIVE_MODE_OPTION_TOOLTIP_TEXTS.ignoreProblemRemovalDisabledNodes
                }
              />
              <ConfigItem
                title={AGGRESSIVE_MODE_OPTION_LABELS.ignoreConsolidatable}
                name={getPrefixedFieldName(
                  FIELD_NAMES.aggressiveModeConfigIgnoreConsolidatable
                )}
                testId="ignore-consolidatable"
                tooltipText={
                  KENT_AGGRESSIVE_MODE_OPTION_TOOLTIP_TEXTS.ignoreConsolidatable
                }
              />
            </Stack>
          </Stack>
          <Stack>
            <Typography variant="P12R" mb={12}>
              Rebalance a node even if it&apos;s running:
            </Typography>
            <Stack gap={12}>
              <ConfigItem
                title={
                  AGGRESSIVE_MODE_OPTION_LABELS.ignoreLocalPersistentVolumes
                }
                name={getPrefixedFieldName(
                  FIELD_NAMES.aggressiveModeConfigIgnoreLocalPersistentVolumes
                )}
                testId="ignore-local-persistent-volumes"
                warning
                tooltipText={
                  KENT_AGGRESSIVE_MODE_OPTION_TOOLTIP_TEXTS.ignoreLocalPersistentVolumes
                }
              />
              {ignoreLocalPVInput.value && <AggressiveModeIgnorePvPodsAlert />}
              <ConfigItem
                title={AGGRESSIVE_MODE_OPTION_LABELS.ignoreRemovalDisabledPods}
                name={getPrefixedFieldName(
                  FIELD_NAMES.aggressiveModeConfigIgnoreProblemRemovalDisabledPods
                )}
                testId="ignore-problem-removal-disabled-pods"
                tooltipText={
                  KENT_AGGRESSIVE_MODE_OPTION_TOOLTIP_TEXTS.ignoreProblemRemovalDisabledPods
                }
              />
              <ConfigItem
                title={
                  AGGRESSIVE_MODE_OPTION_LABELS.ignorePodsWithoutController
                }
                name={getPrefixedFieldName(
                  FIELD_NAMES.aggressiveModeConfigIgnoreProblemPodsWithoutController
                )}
                testId="ignore-problem-pods-without-controller"
                tooltipText={
                  KENT_AGGRESSIVE_MODE_OPTION_TOOLTIP_TEXTS.ignoreProblemPodsWithoutController
                }
              />
              <ConfigItem
                title={AGGRESSIVE_MODE_OPTION_LABELS.ignoreJobPods}
                name={getPrefixedFieldName(
                  FIELD_NAMES.aggressiveModeConfigIgnoreProblemJobPods
                )}
                testId="ignore-problem-job-pods"
                tooltipText={
                  KENT_AGGRESSIVE_MODE_OPTION_TOOLTIP_TEXTS.ignoreProblemJobPods
                }
              />

              <ConfigItem
                title={AGGRESSIVE_MODE_OPTION_LABELS.ignoreBlockingPdb}
                name={getPrefixedFieldName(
                  FIELD_NAMES.aggressiveModeConfigIgnoreBlockingPdb
                )}
                testId="ignore-blocking-pdb"
                tooltipText={
                  KENT_AGGRESSIVE_MODE_OPTION_TOOLTIP_TEXTS.ignoreBlockingPdb
                }
              />
            </Stack>
          </Stack>
        </Stack>
      </Collapse>
    </Stack>
  );
};

export const AggressiveModeSettings = () => {
  const { input: aggressiveModeEnabledInput } = useField<
    FormValues['aggressiveModeEnabled']
  >(FIELD_NAMES.aggressiveModeEnabled);
  const aggressiveModeEnabled = aggressiveModeEnabledInput.value;

  return (
    <Stack gap={16}>
      <FormControlLabel
        sx={{
          ml: 0,
          gap: '8px',
        }}
        control={
          <RffCheckbox
            name={FIELD_NAMES.aggressiveModeEnabled}
            size="small"
            testId="aggressive-mode-enabled"
          />
        }
        label={<Typography variant="P12M">Use Aggressive mode</Typography>}
      />
      {aggressiveModeEnabled && <AggressiveModeConfig />}
    </Stack>
  );
};
